/**
 * OAuthCallbackPage - Completes the Office 365 / Google SSO redirect flow.
 *
 * The backend OAuth callback redirects here with either:
 *   ?token=...  on success
 *   ?error=...  on failure
 *
 * On success the token is stored in localStorage and the app navigates
 * client-side to "/", letting AuthProvider restore the session from the
 * stored token (without a full-page reload that would cancel that restore
 * request). On failure the user is sent back to /login with an error message.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const OAuthCallbackPage: React.FC = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const [message, setMessage] = useState('Completing sign-in...');

	useEffect(() => {
		const token = searchParams.get('token');
		const error = searchParams.get('error');

		if (token) {
			// Persist the token, then hand off to the app via a CLIENT-SIDE
			// navigation. A full-page reload (window.location.replace) tore down
			// the SPA while AuthProvider's session-restore GET /auth/me was in
			// flight — canceling that request and racing to wipe the just-stored
			// token — which dropped the user back on /login. Navigating in-app
			// keeps the SPA alive so AuthProvider restores the session from the
			// stored token normally, exactly like a refresh on an email session.
			localStorage.setItem('auth_token', token);
			localStorage.removeItem('token_expires_at');
			navigate('/', { replace: true });
			return;
		}

		const errorMessage = error || 'Sign-in failed. Please try again.';
		setMessage(errorMessage);
		const timeout = window.setTimeout(() => {
			navigate('/login', { replace: true, state: { oauthError: errorMessage } });
		}, 2500);

		return () => window.clearTimeout(timeout);
	}, [navigate, searchParams]);

	return (
		<div className="o-auth-callback-page min-h-screen min-h-[100dvh] flex items-center justify-center bg-[#eae5e5] px-16px" data-ref="page-o-auth-callback-page">
			<p className="text-brand-text font-sans text-center max-w-md">{message}</p>
		</div>
	);
};

export default OAuthCallbackPage;
